/**
 * ProductCardSkeleton Component
 *
 * Skeleton loading placeholder for ProductCard.
 * Provides a smooth loading experience while product data is being fetched.
 */

import { cn } from '@/lib/utils';

interface ProductCardSkeletonProps {
  /** Additional class names */
  className?: string;
}

/**
 * Skeleton placeholder for ProductCard
 */
export function ProductCardSkeleton({ className }: ProductCardSkeletonProps) {
  return (
    <div
      className={cn(
        'bg-paper-2 rounded-card overflow-hidden border border-line animate-pulse',
        className
      )}
    >
      {/* Header Skeleton */}
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="h-4 w-24 bg-paper-3 rounded" />
        <div className="h-5 w-10 bg-paper-3 rounded-chip" />
      </div>

      {/* Image Area Skeleton */}
      <div className="relative bg-gradient-to-b from-paper-2 to-paper aspect-square">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-3/4 h-3/4 bg-paper-3 rounded-control" />
        </div>
      </div>

      {/* Colors Skeleton */}
      <div className="px-3 py-3">
        <div className="grid grid-cols-5 gap-1.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="w-6 h-6 bg-paper-3 rounded-full" />
          ))}
        </div>
        <div className="grid grid-cols-5 gap-1.5 mt-1.5">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="w-6 h-6 bg-paper-3 rounded-full" />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Grid of skeleton cards for loading state
 */
export function ProductGridSkeleton({
  count = 6,
  className,
}: {
  count?: number;
  className?: string;
}) {
  return (
    <div className={cn('grid grid-cols-3 gap-3', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  );
}

export default ProductCardSkeleton;
